import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button, type ButtonProps } from './Button';

export interface EditArticleButtonProps
  extends Omit<ButtonProps, 'children' | 'onClick' | 'id'> {
  /** ID do artigo (WP post id). Usado para navegar ao editor /artigos/{id}. */
  id?: number | string;
  /** Slug do site (vai como ?site= no editor). */
  site?: string;
  /** Texto do botão (default "{t('common:actions.edit', 'Editar')} artigo"). */
  label?: string;
  /** Sobrepõe a navegação — ex.: abrir o editor em modal no Quadro. */
  onActivate?: () => void;
}

/**
 * Botão padrão "{t('common:actions.edit', 'Editar')} artigo" — entrada única para o editor do CMS em todo o
 * sistema. Por padrão navega para /artigos/{id}?site=…; passe `onActivate` para
 * outro comportamento (ex.: abrir em modal). Visual coeso (atom Button + ícone).
 */
export function EditArticleButton({
  id,
  site,
  label,
  onActivate,
  variant = 'secondary',
  size = 'sm',
  ...rest
}: EditArticleButtonProps) {
  const { t } = useTranslation(['common', 'nav', 'board', 'articles', 'analytics', 'feeddrive', 'config', 'strategist']);
  const navigate = useNavigate();
  const handle = () => {
    if (onActivate) {
      onActivate();
      return;
    }
    if (id != null) {
      navigate(`/artigos/${id}${site ? `?site=${encodeURIComponent(site)}` : ''}`);
    }
  };
  const resolvedLabel = label || `${t('common:actions.edit', 'Editar')} artigo`;
  return (
    <Button
      variant={variant}
      size={size}
      onClick={handle}
      leftIcon={<span aria-hidden></span>}
      {...rest}
    >
      {resolvedLabel}
    </Button>
  );
}
